import { Box, Checkbox, Typography } from "@mui/material";
import FormOptionCard, { FormOptionCardConfig } from "app/components/FormOptionCard";
import { useStyles as useCardStyles } from "app/components/FormOptionCard/styles";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectQuotationSafetyVehicle } from "../../../redux/selector";
import { actions } from "../../../redux/slice";
import { AddOnItemFormValue, QuotationSafetyVehicleState } from "../../../types";
import { useStyles as useAddOnStyles } from "../AddOn/styles";
import { QUOTATION_FORM_I18N_PREFIX } from "../data";
import { FormFieldControl, FormSection } from "../VehicleDetails/FormControls";
import { useStyles } from "./styles";

type CardKey = keyof QuotationSafetyVehicleState;
const P = `${QUOTATION_FORM_I18N_PREFIX}.SAFETY_VEHICLE`;
const tKey = (suffix: string) => `${P}.${suffix}`;

const SAFETY_CARDS: Record<
  Exclude<CardKey, "wings" | "roundIronSides">,
  FormOptionCardConfig
> = {
  spareWheelCarrier: {
    titleKey: tKey("CARDS.SPARE_WHEEL_CARRIER"), fields: ["radio"],
    radioOptions: [
      { value: "REGULAR", labelKey: tKey("OPTIONS.REGULAR") },
      { value: "BASKET", labelKey: tKey("OPTIONS.BASKET") },
    ],
  },
  hazardousMaterialsMounts: {
    titleKey: tKey("CARDS.HAZARDOUS_MATERIALS_MOUNTS"), fields: ["note"],
    notePlaceholderKey: tKey("FIELDS.NOTE"),
  },
  rearBumper: {
    titleKey: tKey("CARDS.REAR_BUMPER"), fields: ["note"],
    notePlaceholderKey: tKey("FIELDS.NOTE"),
  },
  couplingLocks: {
    titleKey: tKey("CARDS.COUPLING_LOCKS"), fields: ["note"],
    notePlaceholderKey: tKey("FIELDS.NOTE"),
  },
  extinguisherMounts: {
    titleKey: tKey("CARDS.EXTINGUISHER_MOUNTS"), fields: ["note"],
    notePlaceholderKey: tKey("FIELDS.NOTE"),
  },
  rearLightMounts: {
    titleKey: tKey("CARDS.REAR_LIGHT_MOUNTS"), fields: ["subCheckbox"],
    subCheckboxLabelKey: tKey("FIELDS.LIGHT_PROTECTION"),
  },
};

const VEHICLE_CARDS: Pick<Record<CardKey, FormOptionCardConfig>, "roundIronSides"> = {
  roundIronSides: {
    titleKey: tKey("CARDS.ROUND_IRON_SIDES"), fields: ["size"],
    sizeLabelKey: tKey("FIELDS.ROUND_IRON_DIAMETER"),
  },
};

const SafetyVehiclePanel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const safetyVehicle = useSelector(selectQuotationSafetyVehicle);
  const styles = useStyles();
  const addOnStyles = useAddOnStyles();
  const cardStyles = useCardStyles();
  if (!safetyVehicle) return null;

  const labels = {
    note: tKey("FIELDS.NOTE"), quantity: tKey("FIELDS.QUANTITY"),
    size: tKey("FIELDS.ROUND_IRON_DIAMETER"), location: tKey("FIELDS.LOCATION"), itemType: tKey("FIELDS.MATERIAL"),
  };
  const update = (key: CardKey, value: Partial<AddOnItemFormValue>) =>
    dispatch(actions.updateQuotationSafetyVehicle({ key, value }));
  const row = (cols: 1 | 3, children: React.ReactNode) => (
    <Box className={`${addOnStyles.cardRow} ${cols === 1 ? addOnStyles.cardRow1 : addOnStyles.cardRow3}`}>
      {children}
    </Box>
  );
  const renderCard = <K extends CardKey>(key: K, config: FormOptionCardConfig) => (
    <FormOptionCard key={key} config={config} value={safetyVehicle[key]} labels={labels}
      onChange={(v) => update(key, v)} />
  );

  const wings = safetyVehicle.wings;
  const materialOptions = [["TIN", tKey("OPTIONS.TIN")]] as [string, string][];
  const locationOptions = [["FRONT", tKey("OPTIONS.FRONT")]] as [string, string][];

  return (
    <>
      <FormSection titleKey={tKey("SECTIONS.SAFETY")} titleClassName={styles.sectionTitle}>
        <Box className={addOnStyles.sectionContent}>
          {row(3, (<>
            {renderCard("rearBumper", SAFETY_CARDS.rearBumper)}
            {renderCard("hazardousMaterialsMounts", SAFETY_CARDS.hazardousMaterialsMounts)}
            {renderCard("spareWheelCarrier", SAFETY_CARDS.spareWheelCarrier)}
          </>))}
          {row(3, (<>
            {renderCard("rearLightMounts", SAFETY_CARDS.rearLightMounts)}
            {renderCard("extinguisherMounts", SAFETY_CARDS.extinguisherMounts)}
            {renderCard("couplingLocks", SAFETY_CARDS.couplingLocks)}
          </>))}
        </Box>
      </FormSection>

      <FormSection titleKey={tKey("SECTIONS.VEHICLE_ADDONS")} titleClassName={styles.sectionTitle}>
        <Box className={addOnStyles.sectionContent}>
          {row(1, (
            <Box className={`${cardStyles.card} ${wings.enabled ? cardStyles.cardActive : ""}`}>
              <Box className={cardStyles.cardHeader}>
                <Checkbox checked={wings.enabled} sx={{ p: 0 }}
                  onChange={(e) => update("wings", { enabled: e.target.checked })} />
                <Typography className={cardStyles.cardTitle}>{t(tKey("CARDS.WINGS"))}</Typography>
              </Box>
              <Box className={cardStyles.cardDivider} />
              <Box className={cardStyles.fieldsRow}>
                <FormFieldControl config={{ field: "itemType", labelKey: tKey("FIELDS.MATERIAL"), type: "select", options: materialOptions }}
                  value={wings.itemType} onChange={(v) => update("wings", { itemType: v })} />
                <FormFieldControl config={{ field: "location", labelKey: tKey("FIELDS.LOCATION"), type: "select", options: locationOptions }}
                  value={wings.location} onChange={(v) => update("wings", { location: v })} />
                <FormFieldControl config={{ field: "quantity", labelKey: tKey("FIELDS.QUANTITY"), type: "number", showSpinners: true, narrow: true }}
                  value={wings.quantity} onChange={(v) => update("wings", { quantity: v })} />
              </Box>
            </Box>
          ))}
          {row(1, renderCard("roundIronSides", VEHICLE_CARDS.roundIronSides))}
        </Box>
      </FormSection>
    </>
  );
};

export default SafetyVehiclePanel;
